import { formatTimeAmPm } from '@/utils/format.js'

const AGENT_SPEAKERS = new Set([
  'agent',
  'assistant',
  'ai',
  'bot',
  'system',
  'ivr',
  'yandle',
  'organization',
  'org',
])

const USER_SPEAKERS = new Set([
  'user',
  'caller',
  'customer',
  'client',
  'human',
  'contact',
  'callee',
])

function pickString(obj, keys) {
  if (!obj || typeof obj !== 'object') return null
  for (const key of keys) {
    const val = obj[key]
    if (val != null && val !== '') {
      const s = String(val).trim()
      if (s) return s
    }
  }
  return null
}

function pickTimeValue(obj) {
  if (!obj || typeof obj !== 'object') return null
  const keys = [
    'time',
    'timestamp',
    'start_time',
    'startTime',
    'start',
    'offset',
    'offset_seconds',
    'offsetSeconds',
    'seconds',
    'duration',
    'at',
  ]
  for (const key of keys) {
    const val = obj[key]
    if (val != null && val !== '') return val
  }
  return null
}

function formatOffsetSeconds(seconds) {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n < 0) return null
  const total = Math.floor(n)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatTimeLabel(raw, callStartTime) {
  if (raw == null || raw === '') return null

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw >= 1e12) {
      const formatted = formatTimeAmPm(raw)
      if (formatted !== '—') return formatted
    }
    if (raw < 86400) return formatOffsetSeconds(raw)
    const formatted = formatTimeAmPm(raw * 1000)
    if (formatted !== '—') return formatted
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return null
    if (/^\d+:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed
    const asNum = Number(trimmed)
    if (Number.isFinite(asNum) && trimmed === String(asNum)) {
      return formatTimeLabel(asNum, callStartTime)
    }
    const formatted = formatTimeAmPm(trimmed)
    if (formatted !== '—') return formatted
    return trimmed
  }

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return formatTimeAmPm(raw)
  }

  if (callStartTime != null) {
    const base = new Date(callStartTime).getTime()
    const d = new Date(raw).getTime()
    if (Number.isFinite(base) && Number.isFinite(d)) {
      const offset = (d - base) / 1000
      if (offset >= 0) return formatOffsetSeconds(offset)
    }
  }

  return String(raw)
}

function resolveSpeakerKind(speakerRaw) {
  if (speakerRaw == null || speakerRaw === '') return 'unknown'
  const key = String(speakerRaw).trim().toLowerCase()
  if (AGENT_SPEAKERS.has(key)) return 'agent'
  if (USER_SPEAKERS.has(key)) return 'user'
  if (key.includes('agent') || key.includes('assistant') || key.includes('bot')) {
    return 'agent'
  }
  if (key.includes('user') || key.includes('caller') || key.includes('customer')) {
    return 'user'
  }
  return 'unknown'
}

function normalizeSegment(item, index, callStartTime) {
  if (item == null) return null

  if (typeof item === 'string') {
    const text = item.trim()
    if (!text) return null
    return {
      id: `seg-${index}`,
      text,
      timeLabel: null,
      speakerLabel: null,
      kind: 'unknown',
    }
  }

  if (typeof item !== 'object') return null

  const text =
    pickString(item, [
      'text',
      'content',
      'message',
      'transcript',
      'utterance',
      'body',
      'value',
    ]) || null

  if (!text) return null

  const speakerRaw = pickString(item, [
    'speaker',
    'role',
    'side',
    'participant',
    'from',
    'type',
    'source',
  ])

  const kind = resolveSpeakerKind(speakerRaw)
  const timeLabel = formatTimeLabel(pickTimeValue(item), callStartTime)

  let speakerLabel = null
  if (kind === 'agent') speakerLabel = 'AI Agent'
  else if (kind === 'user') speakerLabel = 'Customer'
  else if (speakerRaw) speakerLabel = speakerRaw

  return {
    id: item.id ?? item.uuid ?? `seg-${index}`,
    text,
    timeLabel,
    speakerLabel,
    kind,
  }
}

function unwrapTranscriptArray(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.segments)) return data.segments
  if (Array.isArray(data.messages)) return data.messages
  if (Array.isArray(data.utterances)) return data.utterances
  if (Array.isArray(data.transcript)) return data.transcript
  if (Array.isArray(data.data)) return data.data
  return []
}

export function parseCallTranscript(raw, callStartTime = null) {
  if (raw == null || raw === '') return []

  let data = raw
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return []
    try {
      data = JSON.parse(trimmed)
    } catch {
      return [
        {
          id: 'seg-0',
          text: trimmed,
          timeLabel: null,
          speakerLabel: null,
          kind: 'unknown',
        },
      ]
    }
  }

  const list = unwrapTranscriptArray(data)
  if (!list.length && typeof data === 'object' && data !== null) {
    const single = normalizeSegment(data, 0, callStartTime)
    return single ? [single] : []
  }

  return list
    .map((item, index) => normalizeSegment(item, index, callStartTime))
    .filter(Boolean)
}

/** @param {unknown} raw */
export function hasCallTranscript(raw) {
  return parseCallTranscript(raw).length > 0
}
